<template>
  <div>
    <div class="tw:py-6 tw:lg:flex tw:gap-x-2 tw:hidden tw:flex-wrap tw:gap-2">
      <TimeframeFilter
        id="invoices-timeframe-filter"
        ariaLabel="invoices-timeframe-filter"
        size="big"
        class="tw:w-[14%] tw:min-w-[200px]"
        name="timeframe"
        :label="$t('tbs_invoice_timeframe')"
        :options="timeframeOptions"
        :value="timeframeValue"
        @select="onSelectTimeframeOption"
      />
      <div v-if="timeframeValue.id === 'custom-range'">
        <MonthRangePicker
          @setCustomFromDate="setCustomFromDate"
          @setCustomToDate="setCustomToDate"
          :from="toYearMonth(startDate)"
          :to="toYearMonth(endDate)"
        />
      </div>
      <ProductsFilter
        id="invoices-product-filter"
        ariaLabel="invoices-product-filter"
        size="big"
        class="tw:w-[14%] tw:min-w-[200px]"
        name="products"
        :label="$t('tbs_product_filter')"
        :options="productTypeOptions"
        :value="selectedProductValues"
        @select="onSelectedProductsChange"
      />
      <PropertiesFilter
        v-if="accommodationsData.length > 1"
        id="invoices-properties-filter"
        ariaLabel="invoices-properties-filter"
        size="big"
        class="tw:w-[14%] tw:min-w-[200px]"
        name="properties"
        :label="$t('hotels')"
        :options="propertiesOptions"
        :value="selectedPropertyValues"
        @select="onSelectedPropertyChange"
      />
      <StatusFilter
        id="invoices-status-filter"
        ariaLabel="invoices-status-filter"
        size="big"
        class="tw:w-[14%] tw:min-w-[200px]"
        name="status"
        :label="$t('tbs_invoice_status')"
        :options="statusOptions"
        :value="selectedStatusValues"
        @select="onSelectedStatusChange"
      />
    </div>
    <div class="tw:py-6 tw:hidden tw:md:flex tw:lg:hidden">
      <FlyoutFilters
        :currentFilters="currentFlyoutFilters"
        :timeframeOptions="timeframeOptions"
        :timeframeValue="timeframeValue"
        :productsOptions="productTypeOptions"
        :productsValue="selectedProductValues"
        :statusOptions="statusOptions"
        :statusValues="selectedStatusValues"
        :propertiesOptions="propertiesOptions"
        :propertiesValues="selectedPropertyValues"
        :hasPropertiesFilter="accommodationsData.length > 1"
        @applyFilters="applyMobileFilters"
      />
    </div>
    <div class="tw:py-6 tw:flex tw:md:hidden">
      <GhostButton class="tw:flex tw:items-center tw:gap-2" @click="openMobileFilters">
        <FiltersIcon class="filter tw:fill-gray-800 tw:size-[18px]" />
        {{ $t('filters') }}
        <span
          v-if="selectedFiltersAmount > 0"
          class="filter-badge tw:bg-blue-700 tw:text-white tw:rounded-full tw:w-[20px] tw:h-[20px] tw:text-sm"
        >
          {{ selectedFiltersAmount }}
        </span>
      </GhostButton>

      <MobileFilters
        :timeframeOptions="timeframeOptions"
        :timeframeValue="timeframeValue"
        :productsOptions="productTypeOptions"
        :productsValue="selectedProductValues"
        :statusOptions="statusOptions"
        :statusValues="selectedStatusValues"
        :propertiesOptions="propertiesOptions"
        :propertiesValues="selectedPropertyValues"
        :hasPropertiesFilter="accommodationsData.length > 1"
        @applyFilters="applyMobileFilters"
      />
    </div>
  </div>
</template>
<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import TimeframeFilter from '@/components/billing/invoices/filters/TimeframeFilter.vue'
import MonthRangePicker from '@/components/billing/invoices/filters/MonthRangePicker.vue'
import ProductsFilter from '@/components/billing/invoices/filters/ProductsFilter.vue'
import PropertiesFilter from '@/components/billing/invoices/filters/PropertiesFilter.vue'
import StatusFilter from '@/components/billing/invoices/filters/StatusFilter.vue'
import FlyoutFilters from '@/components/billing/invoices/FlyoutFilters.vue'
import MobileFilters from '@/components/billing/invoices/MobileFilters.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import FiltersIcon from '@/components/icons/FiltersIcon.svg?component'

import { useInvoicesFilters } from '@/components/billing/invoices/useInvoicesFilters'
import { storage } from '@/utils/storageService'

const props = defineProps({
  accommodationsData: Array,
})
const {
  startDate,
  endDate,
  selectedPropertyValues,
  selectedStatusValues,
  selectedProductValues,
  statusOptions,
  productOptions,
  proProductOption,
  productTypeOptions,
  selectedFiltersAmount,
  timeframeValue,
  setEndDate,
  setStartDate,
  openMobileFilters,
  closeMobileFilters,
  closeFlyoutFilters,
  resetFilters,
} = useInvoicesFilters()

const currentFlyoutFilters =
  props.accommodationsData.length > 1
    ? [
        { value: 'timeframe', label: 'tbs_invoice_timeframe' },
        { value: 'products', label: 'tbs_product_filter' },
        { value: 'properties', label: 'hotels' },
        { value: 'status', label: 'tbs_invoice_status' },
      ]
    : [
        { value: 'timeframe', label: 'tbs_invoice_timeframe' },
        { value: 'products', label: 'tbs_product_filter' },
        { value: 'status', label: 'tbs_invoice_status' },
      ]

const timeframeOptions = [
  { id: 'last-3-months', label: 'last_3_months', value: '3' },
  { id: 'last-6-months', label: 'last_6_months', value: '6' },
  { id: 'last-12-months', label: 'last_12_months', value: '12' },
  { id: 'custom-range', label: 'tbs_invoice_custom_range', value: 'custom' },
]

const onSelectTimeframeOption = option => {
  timeframeValue.value = option
  setStartDate(option)
  setEndDate()
}
const setCustomFromDate = value => {
  if (!value) {
    startDate.value = null
    return
  }
  const [year, month] = value.split('-').map(Number)
  startDate.value = `${year}-${String(month).padStart(2, '0')}-01`
}

const setCustomToDate = value => {
  if (!value) {
    endDate.value = null
    return
  }

  const [inputYear, inputMonth] = value.split('-').map(Number)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth() + 1

  const isFutureMonth = inputYear > currentYear || (inputYear === currentYear && inputMonth > currentMonth)
  const isCurrentMonth = inputYear === currentYear && inputMonth === currentMonth

  if (isFutureMonth) {
    const year = yesterday.getFullYear()
    const month = String(yesterday.getMonth() + 1).padStart(2, '0')
    const day = String(yesterday.getDate()).padStart(2, '0')
    endDate.value = `${year}-${month}-${day}`
  } else if (isCurrentMonth) {
    const year = yesterday.getFullYear()
    const month = String(yesterday.getMonth() + 1).padStart(2, '0')
    const day = String(yesterday.getDate()).padStart(2, '0')
    endDate.value = `${year}-${month}-${day}`
  } else {
    const lastDayOfMonth = new Date(inputYear, inputMonth, 0).getDate()
    const month = String(inputMonth).padStart(2, '0')
    const day = String(lastDayOfMonth).padStart(2, '0')
    endDate.value = `${inputYear}-${month}-${day}`
  }
}

const hasPRO = ref(false)
let unsubscribeHasPRO = null

onMounted(() => {
  selectedProductValues.value =
    selectedProductValues.value.length > 0 ? selectedProductValues.value : productTypeOptions.value

  // Get initial values
  const hasPROSub = storage.getItem('hasProSubscription')
  const hasPROValue = hasPROSub ? JSON.parse(hasPROSub) : false
  hasPRO.value = hasPROValue

  // Listen for changes
  unsubscribeHasPRO = storage.onChange('hasProSubscription', newValue => {
    hasPRO.value = newValue ? JSON.parse(newValue) : null
  })
})

onUnmounted(() => {
  unsubscribeHasPRO?.()
  resetFilters()
})

watch(hasPRO, newHasPRO => {
  if (newHasPRO) {
    productTypeOptions.value = [...productOptions, proProductOption]
    if (selectedProductValues.value.length === productOptions.length) {
      selectedProductValues.value = [{ id: 0, label: 'campaign_subscription_status_all', value: 'all' }]
    }
  } else {
    productTypeOptions.value = [...productOptions]
    selectedProductValues.value = selectedProductValues.value.filter(option => option.id !== proProductOption.id)
  }
})

watch(
  selectedProductValues,
  newSelectedProductValues => {
    const includesPro = newSelectedProductValues.some(item => item.id === proProductOption.id)
    if (includesPro) {
      productTypeOptions.value = [...productOptions, proProductOption]
    }
  },
  { immediate: true }
)

const onSelectedProductsChange = product => {
  const isAll = product.id === 0
  const hasAll = selectedProductValues.value.some(p => p.id === 0)

  if (isAll) {
    if (!selectedProductValues.value.find(p => p.id === 0) || selectedProductValues.value.length > 1) {
      selectedProductValues.value = [product]
    }
    return
  }

  let newSelection
  if (hasAll) {
    newSelection = [product]
  } else {
    const exists = selectedProductValues.value.find(p => p.id === product.id)
    if (exists) {
      newSelection = selectedProductValues.value.filter(p => p.id !== product.id)
      if (newSelection.length === 0) return
    } else {
      newSelection = [...selectedProductValues.value, product]
    }
  }

  selectedProductValues.value = newSelection
}

const propertiesValues = computed(() => {
  return props.accommodationsData.map(item => {
    return {
      id: item.accommodationId,
      label: item?.name,
      value: item?.accommodationId,
    }
  })
})

const propertiesOptions = computed(() => {
  if (!Array.isArray(props.accommodationsData)) {
    return [{ value: 'all', label: 'property_filter_all', id: 0 }]
  } else {
    return [{ value: 'all', label: 'property_filter_all', id: 0 }, ...propertiesValues.value]
  }
})

const onSelectedPropertyChange = newValue => {
  const isAllOption = newValue.id === 0
  const currentHasAll = selectedPropertyValues.value.some(item => item.id === 0)

  if (isAllOption) {
    selectedPropertyValues.value = [newValue]
  } else {
    if (currentHasAll) {
      selectedPropertyValues.value = selectedPropertyValues.value.filter(item => item.id !== 0)
    }

    const index = selectedPropertyValues.value.findIndex(item => item.id === newValue.id)
    if (index > -1) {
      selectedPropertyValues.value.splice(index, 1)

      if (selectedPropertyValues.value.length === 0) {
        selectedPropertyValues.value = [{ value: 'all', label: 'campaign_subscription_status_all', id: 0 }]
      }
    } else {
      selectedPropertyValues.value.push(newValue)
    }
  }
}

const onSelectedStatusChange = status => {
  const isAllOption = status.id === 0 || status.value === 'all'
  const hasAll = selectedStatusValues.value.some(item => item.id === 0 || item.value === 'all')
  const exists = selectedStatusValues.value.some(item => item.id === status.id)

  const allStatusOptions = statusOptions.value || []
  const totalRegularOptions = allStatusOptions.filter(opt => opt.id !== 0).length
  const allOption = allStatusOptions.find(opt => opt.id === 0)

  if (isAllOption) {
    if (!exists) {
      selectedStatusValues.value = [status]
    } else if (selectedStatusValues.value.length === 1) {
      return
    }
    return
  }

  let newSelection

  if (hasAll) {
    newSelection = [status]
  } else if (exists) {
    newSelection = selectedStatusValues.value.filter(item => item.id !== status.id)
    if (newSelection.length === 0) return
  } else {
    newSelection = [...selectedStatusValues.value, status]
  }

  const regularSelectedCount = newSelection.filter(item => item.id !== 0).length

  if (regularSelectedCount === totalRegularOptions) {
    selectedStatusValues.value = [allOption]
  } else {
    selectedStatusValues.value = newSelection
  }
}

const applyMobileFilters = (temporaryFilters, isReset, type) => {
  const filtersType = Object.keys(temporaryFilters)

  if (filtersType.includes('timeframe')) {
    onSelectTimeframeOption(temporaryFilters.timeframe)
  }
  if (filtersType.includes('timeframe') && (filtersType.includes('startDate') || filtersType.includes('endDate'))) {
    onSelectTimeframeOption(temporaryFilters.timeframe)
    setCustomFromDate(temporaryFilters.startDate)
    setCustomToDate(temporaryFilters.endDate)
  }
  if (filtersType.includes('status')) {
    selectedStatusValues.value = temporaryFilters.status
  }
  if (filtersType.includes('products')) {
    selectedProductValues.value = temporaryFilters.products
  }
  if (filtersType.includes('properties')) {
    selectedPropertyValues.value = temporaryFilters.properties
  }

  selectedFiltersAmount.value = getFiltersNumber(filtersType)
  if (!isReset && type === 'mobile') {
    closeMobileFilters()
  }
  if (!isReset && type === 'flyout') {
    closeFlyoutFilters()
  }
}

const getFiltersNumber = filters => {
  if (filters.includes('startDate') && filters.includes('endDate') && filters.includes('timeframe'))
    return filters.length - 2
  else return filters.length
}

const toYearMonth = dateString => {
  return dateString.split('-').slice(0, 2).join('-')
}
</script>
